import { useCallback, useState } from 'react';
import { BodyContainer } from '../../../elements';
import { ConsolidatedReplenishmentHook } from '../../../hooks/inventory';
import { useSecondaryHeader } from '../../../hooks/useSecondaryHeader';
import { InventoryService, ToastMessage } from '../../../utility';
import { ReplenishmentAdjustmentPopup } from '../../../page-components/inventory/ReplenishmentPopup';
import { ReplenishmentListTable } from '../../../page-components/inventory/replenishment/ReplenishmentListTable';
import { ReplenishmentListToolbar } from '../../../page-components/inventory/replenishment/ReplenishmentListToolbar';
import { useGlobalContext } from '../../../contexts';

export function ReplenishmentListScreen() {
  const [adjustmentItem, setAdjusmentItem] = useState(null);
  const { warehouseId } = useGlobalContext();
  const [actionLoading, setActionLoading] = useState({});
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [viewIgnoredItems, setViewIgnoredItems] = useState(false);

  const openAdjustmentPopup = (item, selectedInventoryId = null) => {
    if (selectedInventoryId) {
      setAdjusmentItem({ ...item, selectedInventoryId });
    } else {
      setAdjusmentItem(item);
    }
  };

  const closeAdjustmentPopup = () => {
    setAdjusmentItem(null);
  };

  const { data, loading, totalData, filter, filterChanged, pageChanged, refresh } =
    ConsolidatedReplenishmentHook(warehouseId, viewIgnoredItems);

  const onExport = useCallback(() => {
    setDownloadLoading(true);
    const params = {
      limit: filter.limit,
      page: filter.page,
      warehouseId,
    };
    if (filter.search?.trim()) {
      params.search = filter.search.trim();
    }
    if (filter.customerOrganizationId?.trim()) {
      params.customerOrganizationId = filter.customerOrganizationId.trim();
    }

    InventoryService.replenishmentInventoryConsolidatedExport(params)
      .then(response => {
        const disposition = response.headers['content-disposition'];
        let filename = 'export.csv';
        const match = disposition?.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match && match[1]) {
          filename = match[1].replace(/['"]/g, '');
        }

        const blob = new Blob([response.data], {
          type: 'text/csv;charset=utf-8;',
        });

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      })
      .catch(err => {
        ToastMessage.apiError(err);
      })
      .finally(() => {
        setDownloadLoading(false);
      });
  }, [filter, warehouseId]);

  const ignoreItem = useCallback(
    async item => {
      try {
        setActionLoading(loaders => ({
          ...loaders,
          [item._id]: true,
        }));

        await InventoryService.ignoreItem(item?.productId?._id);
        ToastMessage.success('Item successfully added to ignored list');
        refresh();
        setTimeout(() => {
          setActionLoading(loaders => ({
            ...loaders,
            [item._id]: false,
          }));
        }, 600);
      } catch (error) {
        ToastMessage.apiError(error);
        setActionLoading(loaders => ({
          ...loaders,
          [item._id]: false,
        }));
      }
    },
    [refresh],
  );

  const activateItem = useCallback(
    async item => {
      try {
        setActionLoading(loaders => ({
          ...loaders,
          [item._id]: true,
        }));

        await InventoryService.activateItem(item?.productId?._id);
        ToastMessage.success('Item successfully activated');
        refresh();
        setTimeout(() => {
          setActionLoading(loaders => ({
            ...loaders,
            [item._id]: false,
          }));
        }, 600);
      } catch (error) {
        ToastMessage.apiError(error);
        setActionLoading(loaders => ({
          ...loaders,
          [item._id]: false,
        }));
      }
    },
    [refresh],
  );

  const onToggleIgnoredItems = useCallback(() => {
    setViewIgnoredItems(prev => !prev);
  }, []);

  const onCustomerSelect = useCallback(
    customer => {
      filterChanged('customerOrganizationId', customer.customerOrganizationId);
    },
    [filterChanged],
  );

  const onSearchChange = useCallback(
    value => {
      filterChanged('search', value);
    },
    [filterChanged],
  );

  useSecondaryHeader(
    () => (
      <ReplenishmentListToolbar
        viewIgnoredItems={viewIgnoredItems}
        onToggleIgnoredItems={onToggleIgnoredItems}
        downloadLoading={downloadLoading}
        onExport={onExport}
        onCustomerSelect={onCustomerSelect}
        search={filter.search ?? ''}
        onSearchChange={onSearchChange}
      />
    ),
    [
      viewIgnoredItems,
      onToggleIgnoredItems,
      downloadLoading,
      onExport,
      onCustomerSelect,
      filter.search,
      onSearchChange,
    ],
  );

  return (
    <BodyContainer className="no-header">
      <ReplenishmentListTable
        data={data}
        loading={loading}
        filter={filter}
        totalData={totalData}
        pageChanged={pageChanged}
        filterChanged={filterChanged}
        isIgnoredList={viewIgnoredItems}
        actionLoading={actionLoading}
        noDataMessage={viewIgnoredItems ? 'No Ignored Item(s)' : 'No Active Item(s)'}
        openPopup={openAdjustmentPopup}
        ignoreItem={ignoreItem}
        activateItem={activateItem}
      />
      <ReplenishmentAdjustmentPopup
        onClose={closeAdjustmentPopup}
        open={!!adjustmentItem}
        item={adjustmentItem}
        refresh={refresh}
        warehouseId={warehouseId}
      />
    </BodyContainer>
  );
}
